const hasValue = (value) => value !== null && value !== undefined && value !== "";

export const normalizeBillingSupplierSource = (value) => {
  const source = String(value || "").trim().toLowerCase();
  if (["asi-supplier", "asi", "supplier"].includes(source)) return "asi-supplier";
  if (["core-supplier", "core", "internal", "internal-supplier", "customsupplier", "custom-supplier"].includes(source)) return "core-supplier";
  return "";
};

export const supplierSourceToSearchTab = (source) => {
  const normalized = normalizeBillingSupplierSource(source);
  if (normalized === "asi-supplier") return "all";
  return "core";
};

export const searchTabToSupplierSource = (tab) =>
  tab === "all" ? "asi-supplier" : "core-supplier";

export const applyBillingSupplierSearchSelection = (supplier, tab) => {
  if (!supplier) return null;
  const supplierSource = searchTabToSupplierSource(tab);
  return { ...supplier, supplierSource, SupplierSource: supplierSource };
};

export const readBillingSupplierMeta = (value) => {
  if (!value) return {};
  if (typeof value !== "string") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : { Name: value };
  } catch {
    return { Name: value };
  }
};

export const resolveBillingSupplierIdentity = ({ bill = {}, po = null } = {}) => {
  const meta = readBillingSupplierMeta(bill?.billSupplier ?? bill?.billSupplierInfo);
  const supplierId = bill?.supplierId ?? meta?.supplierId ?? meta?.id ?? "";
  const supplierName = String(meta?.Name ?? meta?.name ?? bill?.supplierName ?? bill?.supplierInfo ?? "").trim();
  let supplierSource = normalizeBillingSupplierSource(
    meta?.supplierSource ?? meta?.SupplierSource ?? bill?.supplierSource ?? bill?.accountingSupplierSource
  );

  if (!supplierSource && (meta?.AsiNumber || meta?.asiNumber)) supplierSource = "asi-supplier";

  const poMeta = readBillingSupplierMeta(po?.supplierInfo);
  const poId = po?.supplierId ?? poMeta?.supplierId ?? poMeta?.vendorSupplierId ?? poMeta?.id;
  const poName = String(poMeta?.supplierName ?? poMeta?.SupplierName ?? poMeta?.Name ?? po?.supplierName ?? "").trim();
  const sameId = hasValue(supplierId) && hasValue(poId) && String(supplierId) === String(poId);
  const sameName = !supplierName || !poName || supplierName.toLowerCase() === poName.toLowerCase();
  if (!supplierSource && sameId && sameName) {
    supplierSource = normalizeBillingSupplierSource(
      po?.supplierSource ??
      po?.SupplierSource ??
      poMeta?.supplierSource ??
      poMeta?.SupplierSource ??
      poMeta?.source ??
      poMeta?.Source
    );
  }

  if (!supplierSource && sameId && sameName && (poMeta?.AsiNumber || poMeta?.asiNumber)) {
    supplierSource = "asi-supplier";
  }

  const normalizedId = hasValue(supplierId) ? String(supplierId) : "";
  const isLegacyUnresolved = Boolean(normalizedId && !supplierSource);
  const selectedSupplierOption = normalizedId && supplierName ? {
    id: normalizedId,
    Id: normalizedId,
    Name: supplierName,
    name: supplierName,
    supplierSource,
    SupplierSource: supplierSource,
    supplierKey: supplierSource ? `${supplierSource}:${normalizedId}` : normalizedId,
    ...(isLegacyUnresolved ? { legacyUnresolvedSource: true } : {}),
  } : null;

  return { supplierId: normalizedId, supplierName, supplierSource, selectedSupplierOption, isLegacyUnresolved };
};

export const buildBillingSupplierMetadata = ({ supplierId, supplierName, supplierSource, existing = null }) => {
  const id = hasValue(supplierId) ? String(supplierId) : "";
  const name = String(supplierName || "").trim();
  const source = normalizeBillingSupplierSource(supplierSource);
  if (!id && !name) return existing;
  return {
    ...readBillingSupplierMeta(existing),
    type: "supplier",
    id: id || null,
    supplierId: id || null,
    Name: name || null,
    ...(source ? { supplierSource: source, SupplierSource: source } : {}),
  };
};
